import {
  useWSModalCustom,
  WSAlert,
  WSForm,
  WSButton,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSSectionToolbar,
  WSSelectOption,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Payer,
  PayerEngagement,
  PayerRowEngagement,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  queriesPayerEngagements,
  queriesPayerRows,
  queriesPayers,
  queriesWorkLoggingConfigs,
  queriesWorkLogs
} from "@query";
import { selectorCounterpartyDisplayName } from "../../../shared/selectors/selectorCounterpartyDisplayName";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { INVOICING_PATH } from "../../Invoicing";
import { PAYERS_PATH } from "../../Payers/paths";
import { SelectPayerEngagement } from "../../Payers/components/SelectPayerEngagement";
import { WorkLogEngagementDetails } from "../components/WorkLogEngagementDetails";
import {
  routeHelpersWorkLogPayeeDetails,
  routeHelpersWorkLogsPayeeInProgress
} from "../paths";

type FormData = {
  /** The picked client+assignment pair; `null` until one is chosen. */
  payerEngagement: Nullable<WSSelectOption>;
};

/**
 * Only assignments whose engagement carries work definitions can hold a work
 * log — the rule the search index projects onto
 * `PayerRowEngagement.isWorkLoggingEnabled`.
 *
 * This used to ride along as an `engagements.isWorkLoggingEnabled` filter on the
 * search request, which matches whole *client* rows: a client with no
 * work-logging assignment was dropped before the UI saw it, and the picker then
 * re-applied the same rule to the assignments of the clients that survived. The
 * filter is gone from the request — every client assignment is fetched — and the
 * rule lives here alone, so what is on offer no longer depends on the search
 * service answering that filter.
 */
const isWorkLoggingEngagement = (engagement: PayerRowEngagement) =>
  !!engagement.isWorkLoggingEnabled;

/**
 * Mirrors the selection into form state.
 *
 * The selection is held in component state because the queries below it (payer,
 * engagement, work-logging config, existing work logs) need it at the top level,
 * and because two things set it programmatically — the preselect effect and the
 * add-client modal. This keeps the form's copy in step whatever set it, so Yup
 * validates against what the user actually sees.
 */
const SyncSelection: React.FC<{ value: Nullable<WSSelectOption> }> = ({
  value
}) => {
  const { setValue } = useFormContext();

  useEffect(() => {
    setValue("payerEngagement", value ?? null);
  }, [value, setValue]);

  return null;
};

type Props = {
  defaultPayerId?: string;
  defaultPayerPayeeEngagementId?: string;
} & WSModalCustomProps<void>;

export const ModalCreateWorkLogPayee: React.FC<Props> = ({
  defaultPayerId,
  defaultPayerPayeeEngagementId,
  ...props
}) => {
  const history = useHistory();
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const isPreselected = !!defaultPayerId && !!defaultPayerPayeeEngagementId;

  const { payerId, payerPayeeEngagementId } = useMemo(() => {
    if (!selectValue) {
      // Use preselected values while loading (before selectValue is set)
      if (isPreselected) {
        return {
          payerId: defaultPayerId,
          payerPayeeEngagementId: defaultPayerPayeeEngagementId
        };
      }
      return {};
    }
    const [payerId, engagementId] = selectValue.value.split("/");
    return { payerId, payerPayeeEngagementId: engagementId };
  }, [
    isPreselected,
    defaultPayerId,
    defaultPayerPayeeEngagementId,
    selectValue
  ]);

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );
  const queryEngagement = queriesPayerEngagements.getPayerEngagement.useQuery(
    { payerId: payerId!, payerEngagementId: payerPayeeEngagementId! },
    { enabled: !!payerId && !!payerPayeeEngagementId }
  );
  // The client name comes off the search row (server-resolved
  // `profile.resolvedName`), so the preselected label matches the label the
  // picker shows for the same client. The `Payer` read above stays for the
  // engagement details below (company / contact email).
  const queryPreselectedPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    { filter: { id: { eq: payerId! } }, page: { size: 1 } },
    { enabled: !!payerId, retry: false }
  );

  // Initialize selectValue when preselected values are provided
  useEffect(() => {
    const preselectedPayerRow = queryPreselectedPayerRow.data?.data?.[0];

    if (
      isPreselected &&
      preselectedPayerRow &&
      queryEngagement.data &&
      !selectValue
    ) {
      const payerName = selectorSearchRowName(preselectedPayerRow);
      const engagementName = queryEngagement.data.engagementName;

      setSelectValue({
        value: `${defaultPayerId}/${defaultPayerPayeeEngagementId}`,
        label: `${payerName} • ${engagementName}`,
        // TODO (v3-migration): payer.email is empty for active payers, no wsName email source
        labels: [preselectedPayerRow.email].filter(Boolean) as string[],
        avatar: {
          type: "text",
          text: payerName
        }
      });
    }
  }, [
    isPreselected,
    queryPreselectedPayerRow.data,
    queryEngagement.data,
    defaultPayerId,
    defaultPayerPayeeEngagementId,
    selectValue
  ]);

  // Every status that still occupies the engagement's slot for a pay period.
  // Rejected and discarded work logs are deliberately absent — those are exactly
  // the cases where a fresh work log for the same period is the right move.
  //
  // TODO(v3): dropped the v1 `filter[endDate][exists]=false` filter — v3
  // listWorkLogs has no exists-filter and periodStart/periodEnd are readonly, so
  // the period match below is done client-side instead.
  const queryExistingWorkLogs = queriesWorkLogs.listWorkLogs.usePaginatedQuery(
    {
      filter: {
        payeeEngagementId: { eq: payerPayeeEngagementId },
        status: {
          anyOf: [
            WorkLogStatus.DRAFT,
            WorkLogStatus.IN_PROGRESS,
            WorkLogStatus.IN_REVIEW,
            WorkLogStatus.APPROVED
          ]
        }
      }
    },
    { enabled: !!payerPayeeEngagementId }
  );

  const existingWorkLogs = queryExistingWorkLogs.flatData || [];

  // The secondary "you already have drafts" alert is only about work the payee
  // can still pick up, so it stays scoped to drafts.
  const existingDraftWorkLogs = existingWorkLogs.filter(
    existing => existing.status === WorkLogStatus.DRAFT
  );

  // Only one work log per pay period per engagement — a second one for the same
  // period can't be created, so the modal switches to opening the existing one.
  // A work log with no period yet is the current period's by construction (the
  // period is server-derived), which is why it counts here.
  const existingCurrentPeriodWorkLog = existingWorkLogs.find(existing => {
    const { periodStart, periodEnd } = existing;

    if (!periodStart || !periodEnd) {
      return true;
    }

    const now = Date.now();
    return periodStart.getTime() <= now && now <= periodEnd.getTime();
  });

  const queryWorkLogConfig =
    queriesWorkLoggingConfigs.getWorkLoggingConfigForPayee.useQuery(
      { payerAccountId: payerId! },
      { enabled: !!payerId }
    );

  const isPayeeSubmissionDisabled =
    !!payerId &&
    !queryWorkLogConfig.isLoading &&
    queryWorkLogConfig.data?.allowPayeeCreatedWorkLogs?.enabled === false;

  // Whether the picker can offer anything. This was a *filtered* search (one
  // page of `engagements.isWorkLoggingEnabled` rows, counted); with the filter
  // moved into the UI it has to read every client and apply the rule itself —
  // hence `useQueryAll`, since the one work-logging assignment a payee has can
  // sit on any page.
  const querySelectablePayerRows = queriesPayerRows.searchPayerRows.useQueryAll(
    {}
  );

  const hasNoSelectableAssignments =
    !querySelectablePayerRows.isLoading &&
    !(querySelectablePayerRows.flatData ?? []).some(payerRow =>
      (payerRow.engagements ?? []).some(isWorkLoggingEngagement)
    );

  const [createWorkLog, createWorkLogMeta] =
    queriesWorkLogs.createWorkLog.useMutation();

  const handleAddNewPayer = useCallback(
    (payer: Payer, engagements: PayerEngagement[]) => {
      if (engagements.length > 0) {
        const payerEngagement = engagements[0];
        // The client was just created by the add-client modal, so no search row
        // is indexed for it yet — this is the one place the name has to come off
        // the `Payer` the modal returned.
        const payerName = selectorCounterpartyDisplayName(payer);

        setSelectValue({
          value: `${payer.id}/${payerEngagement.id}`,
          label: `${payerName} • ${payerEngagement.engagementName}`,
          labels: payer.email ? [payer.email] : [],
          avatar: {
            type: "text",
            text: payerName
          }
        });
      }
    },
    []
  );

  const handleViewExistingWorkLogs = useCallback(() => {
    history.push(
      routeHelpersWorkLogsPayeeInProgress.createLocationObject({
        search: { filter: { assignmentId: payerPayeeEngagementId } }
      })
    );
    props.onClose();
  }, [history, payerPayeeEngagementId, props]);

  const handleEditExistingWorkLog = useCallback(() => {
    if (!existingCurrentPeriodWorkLog?.id) {
      return;
    }

    history.push(
      routeHelpersWorkLogPayeeDetails.createLocationObject({
        workLogId: existingCurrentPeriodWorkLog.id
      })
    );
    props.onClose();
  }, [history, existingCurrentPeriodWorkLog, props]);

  const handleCreate = () => {
    if (!payerPayeeEngagementId) return;

    createWorkLog(
      {
        // TODO(v3): WorkLogCreate has no startDate/endDate/userId — the
        // period is server-derived, so the v1 `startDate: new Date()` is dropped.
        workLogCreate: {
          payeeEngagementId: payerPayeeEngagementId
        }
      },
      {
        onSuccess: workLog => {
          history.push(
            routeHelpersWorkLogPayeeDetails.createLocationObject({
              workLogId: workLog.id!
            })
          );
          props.onClose();
        }
      }
    );
  };

  return (
    <WSForm<FormData>
      defaultValues={{ payerEngagement: null }}
      validationSchema={Yup.object().shape({
        payerEngagement: Yup.mixed()
          .nullable()
          .required("Select the client assignment to log work for")
      })}
      onSubmit={handleCreate}
    >
      {({ handleSubmit: submitForm }) => (
        <WSModal
          title="New work log"
          actions={{
            alignment: "fill",
            buttons: [
              {
                kind: "Tertiary",
                label: "Cancel",
                type: "button",
                onClick: () => props.onClose()
              },
              {
                kind: "Primary",
                label: existingCurrentPeriodWorkLog
                  ? "Edit existing work log"
                  : "Create new work log",
                // Submitted by calling the form handler rather than by button type:
                // `WSModal` renders its content in a portal, so a native submit
                // button in the footer never reaches the <form> element `WSForm`
                // creates. React context still crosses the portal, so the fields and
                // validation work either way — only the DOM submit event does not.
                type: "button",
                onClick: existingCurrentPeriodWorkLog
                  ? handleEditExistingWorkLog
                  : submitForm(handleCreate),
                loading: createWorkLogMeta.isLoading,
                disabled:
                  hasNoSelectableAssignments ||
                  !payerPayeeEngagementId ||
                  queryPayer.isLoading ||
                  queryPreselectedPayerRow.isLoading ||
                  queryEngagement.isLoading ||
                  queryExistingWorkLogs.isLoading ||
                  queryWorkLogConfig.isLoading ||
                  isPayeeSubmissionDisabled
              }
            ]
          }}
          divider
          {...props}
        >
          <WSList gap="L">
            <SyncSelection value={selectValue} />

            <WSElement>
              <WSSectionToolbar title="Client assignment *" mb="XS" />
              {hasNoSelectableAssignments ? (
                <WSEmptyState
                  type="search"
                  orientation="horizontal"
                  hasBorder
                  title="No assignments available"
                  description="Only assignments you can create work logs for will appear here"
                />
              ) : (
                <>
                  <WSText.ParagraphSm color="gray500" mb="M">
                    Select the client assignment to log work for
                  </WSText.ParagraphSm>
                  <WSForm.Field
                    component={SelectPayerEngagement}
                    componentProps={{
                      // Not a JSX attribute any more, so jsx-a11y/no-autofocus
                      // no longer applies — focus still lands on the picker
                      // unless the assignment arrived preselected.
                      autoFocus: !isPreselected,
                      onAddNewPayer: handleAddNewPayer,
                      // The work-logging rule, applied to the fetched rows
                      // instead of to the search request (see
                      // `isWorkLoggingEngagement`).
                      engagementFilter: isWorkLoggingEngagement,
                      menuFooterText:
                        "Don't see an assignment? Some assignments are managed by clients directly.",
                      customMenuFooterAction: {
                        label: "View clients",
                        rightIcon: "chevron-right",
                        onClick: () => {
                          history.push(PAYERS_PATH);
                          props.onClose();
                        }
                      }
                    }}
                    name="payerEngagement"
                    onChange={setSelectValue}
                  />
                </>
              )}
            </WSElement>

            <WSElement colorBackground="gray50" px="M" py="S">
              <WSText.ParagraphXs weight="medium" color="gray600" mb="XS">
                Don't see an assignment?
              </WSText.ParagraphXs>
              <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
                <WSText.ParagraphXs color="gray400">
                  Some assignments use direct invoicing
                </WSText.ParagraphXs>
                <WSButton
                  kind="Link"
                  size="S"
                  rightIcon="chevron-right"
                  onClick={() => {
                    history.push(INVOICING_PATH + "/create");
                    props.onClose();
                  }}
                >
                  Create invoice
                </WSButton>
              </WSFlexBox.CenterY>
              <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
                <WSText.ParagraphXs color="gray400">
                  Some assignments are managed by clients directly.
                </WSText.ParagraphXs>
                <WSButton
                  kind="Link"
                  size="S"
                  rightIcon="chevron-right"
                  onClick={() => {
                    history.push(PAYERS_PATH);
                    props.onClose();
                  }}
                >
                  View clients
                </WSButton>
              </WSFlexBox.CenterY>
            </WSElement>

            {!hasNoSelectableAssignments && payerPayeeEngagementId && (
              <WorkLogEngagementDetails
                shimmer={queryPayer.isLoading || queryEngagement.isLoading}
                payer={queryPayer.data}
                payerPayeeEngagement={queryEngagement.data}
              />
            )}

            {!hasNoSelectableAssignments && isPayeeSubmissionDisabled && (
              <WSAlert
                theme="info"
                icon="info-circle"
                title="Client does not allow work log submissions"
              >
                Only the client can create work logs on your behalf for this
                assignment.
              </WSAlert>
            )}

            {!hasNoSelectableAssignments && existingCurrentPeriodWorkLog && (
              <WSAlert
                theme="warning"
                icon="alert"
                title="Existing work log found for this pay period"
              >
                You can only create one work log per pay period for this
                engagement, so you should complete the existing work log.
              </WSAlert>
            )}

            {!hasNoSelectableAssignments &&
              !existingCurrentPeriodWorkLog &&
              existingDraftWorkLogs.length > 0 && (
                <WSAlert
                  theme="info"
                  icon="info-circle"
                  title="Existing work logs found"
                  action={{
                    text: "View existing work logs",
                    onClick: handleViewExistingWorkLogs
                  }}
                >
                  You have {existingDraftWorkLogs.length} in-progress work log
                  {existingDraftWorkLogs.length > 1 ? "s" : ""} for this
                  assignment. Would you like to continue working on those, or
                  start a fresh work log?
                </WSAlert>
              )}

            <ErrorAlert context="WorkLog" error={createWorkLogMeta.error} />
          </WSList>
        </WSModal>
      )}
    </WSForm>
  );
};

export const useModalCreateWorkLogPayee = () =>
  useWSModalCustom(ModalCreateWorkLogPayee);
